// @flow

import type Map from './map';
import {Common} from '../util/enums';
import {bindAll, warnOnce, extend, convertToSelectedUnit} from '../util/util';
import {loadSVGImage} from '../util/image';
import {getJSON, postData} from '../util/ajax';
import config from '../util/config';
import LngLat from '../geo/lng_lat';
import LngLatBounds from '../geo/lng_lat_bounds';
import {Event, Evented, ErrorEvent} from '../util/evented';
import {MapMouseEvent} from './events';
import type {RouteOptions} from './server_side_structs';
import {postRouteRequestParameters, getRouteRequestParameters} from './server_side_structs';
import {DragRouteHandler, defaultDragRouteOptions} from './drag_route';
import UndoRedoStack from '../util/undo_redo_stack';
import getRouteReports from './route_report';
import type {ArrowOptions} from './route_arrow';
import {RouteArrow} from './route_arrow';
import type {IconOptions} from './route_icon';
import {defaultOriginIconOptions, defaultDestIconOptions, defaultStopIconOptions, createStopIcon} from './route_icon';
import type {RouteWidth} from './route_width';
import {defaultRouteWidth} from './route_width';

/**
 * Creates a route component
 * @param options
 * @param {string} [options.routeId] A unique string values used to identify a route. It is needed when removing or updating a route.
 * @param {string} [options.routeColor] The color of the route line
 * @param {number} [options.routePathOpacity] The opacity of the route line
 * @param {RouteWidth|number} [options.routeWidth] The width of the route line. Example 20 or {stops: [[10, 7],[15, 9],[19, 11]]}
 * @param {Array<LngLatLike | StopOptions>} [options.stops] An array of at least two <code>TrimbleMaps.LngLat</code> or <code>StopOptions</code> objects containing the locations of the stops of the route. When StopOptions are used, the <code>requestMethod</code> parameter will be ignored and the type will be POST.
 * @param {Common.VehicleType} [options.vehicleType] Default: <code>TrimbleMaps.Common.VehicleType.TRUCK</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> <br> Restricts the route to roads that are accessible by the selected vehicle type. <ul><li><code>TrimbleMaps.Common.VehicleType.TRUCK</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Truck </li><li><code>TrimbleMaps.Common.VehicleType.LIGHT_TRUCK</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Light Truck </li><li><code>TrimbleMaps.Common.VehicleType.AUTOMOBILE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Automobile</li></ul>
 * @param {Common.RouteType} [options.routeType] Default: <code>TrimbleMaps.Common.RouteType.PRACTICAL</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> <br> Type of route. <ul><li><code>TrimbleMaps.Common.RouteType.PRACTICAL</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Practical </li><li><code>TrimbleMaps.Common.RouteType.SHORTEST</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Shortest </li><li><code>TrimbleMaps.Common.RouteType.FASTEST</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Fastest</li></ul>
 * @param {Common.RouteOptimization} [options.routeOptimization] Default: <code>TrimbleMaps.Common.RouteOptimization.NONE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> <br> <ul><li><code>TrimbleMaps.Common.RouteOptimization.NONE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - None </li><li><code>TrimbleMaps.Common.RouteOptimization.OPTIMIZE_ALL_STOPS</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Optimize all stops </li><li><code>TrimbleMaps.Common.RouteOptimization.OPTIMIZE_INTERMEDIATE_STOPS</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Optimize intermediate stops</li></ul>
 * @param {boolean} [options.tollDiscourage] Default: <code>false</code> <br> Generates the route by trying to avoid toll roads. Deprecated.
 * @param {Common.TollRoadsType} [options.tollRoads] Defaut is null. If it is null, tollDiscourage will be used.
 * @param {Array<number>} [options.afSetIDs] The IDs of the Route Modifier (avoid/favor) sets that should be applied to the route. The format is comma separated IDs. The IDs are integers. Set to [-1] to select all sets.
 * @param {Array<string>} [options.afSetNames] The names of the Route Modifier (avoid/favor) sets that should be applied to the route. Default is an empty set.
 * @param {boolean} [options.bordersOpen] Default: <code>true</code> <br> Indicates whether borders are open for travel.
 * @param {boolean} [options.overrideRestrict] Default: <code>false</code> <br> Indicates whether or not to override truck restrictions.
 * @param {boolean} [options.highwayOnly] Default: <code>false</code> <br> Restricts the route to only highway roads.
 * @param {Common.HazMatType} [options.hazMatType] Default: <code>TrimbleMaps.Common.HazMatType.NONE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> <br> Restricts the route to roads that allow transportation of the selected hazardous material. <ul><li><code>TrimbleMaps.Common.HazMatType.NONE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - None</li><li><code>TrimbleMaps.Common.HazMatType.GENERAL</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - General</li><li><code>TrimbleMaps.Common.HazMatType.CAUSTIC</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Caustic</li><li><code>TrimbleMaps.Common.HazMatType.EXPLOSIVES</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Explosives</li><li><code>TrimbleMaps.Common.HazMatType.FLAMMABLE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Flammable</li><li><code>TrimbleMaps.Common.HazMatType.INHALANTS</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Inhalants</li><li><code>TrimbleMaps.Common.HazMatType.RADIOACTIVE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Radioactive</li><li><code>TrimbleMaps.Common.HazMatType.HARMFUL_TO_WATER</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - HarmfulToWater</li><li><code>TrimbleMaps.Common.HazMatType.TUNNEL</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Tunnel</li></ul><br>Deprecated. Use hazMatTypes instead.
 * @param {Array<Common.HazMatType>} [options.hazMatTypes] Indicates the hazardous material types.
 * @param {Array<Common.TunnelCategory>} [options.tunnelCategories] Represents the EU Tunnel categories.
 * @param {Array<RoadSpeedBase>} [options.customRoadSpeeds] A list of custom road speeds to be applied for the route.
 * @param {Common.DistanceUnit} [options.distanceUnits] Default: <code>TrimbleMaps.Common.DistanceUnit.MILES</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> <br> Distance units as miles or kilometers. <ul><li><code>TrimbleMaps.Common.DistanceUnit.MILES</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Miles </li><li><code>TrimbleMaps.Common.DistanceUnit.KILOMETERS</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Kilometers</li></ul>
 * @param {boolean} [options.trkLCV] Indicates whether the truck is a multi-trailer or longer combination vehicle.
 * @param {Common.TruckUnit} [options.trkUnits] Default: <code>TrimbleMaps.Common.TruckUnit.ENGLISH</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> <br> Sets truck dimensions as English or Metric. <ul><li><code>TrimbleMaps.Common.TruckUnit.ENGLISH</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - English </li><li><code>TrimbleMaps.Common.TruckUnit.METRIC</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Metric</li></ul>
 * @param {number} [options.trkHeight] The height of the vehicle in feet and inches or meters depending on units. Example 13'6" or 4.1148.
 * @param {number} [options.trkLength] The length of the vehicle in feet and inches or meters depending on units. Example 13'6" or 4.1148.
 * @param {number} [options.trkWidth] The width of the vehicle in feet and inches or meters depending on units. Example 13'6" or 4.1148.
 * @param {number} [options.trkWeight] The weight of the vehicle in pounds or kilograms depending on units.
 * @param {number} [options.trkAxles] Default: 5 <br>The number of axles on the vehicle; acceptable values are 2 through 14.
 * @param {number} [options.trkMaxWeightPerAxleGroup] Weight per axle
 * @param {number} [options.trkTireCount] Tire count
 * @param {boolean} [options.ferryDiscourage] Default: <code>false</code> <br> Indicates whether or not to discourage the use of ferries when creating the route.
 * @param {boolean} [options.inclFerryDist] Default: <code>true</code> <br> Indicates whether or not to include ferry distances in mileage calculations of reports.
 * @param {Common.FuelUnit} [options.fuelUnits] Default: <code>TrimbleMaps.Common.FuelUnit.GALLONS</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> <br> Fuel units as gallons and liters. <ul><li><code>TrimbleMaps.Common.FuelUnit.GALLONS</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Gallons </li><li><code>TrimbleMaps.Common.FuelUnit.LITERS</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Liters
 * @param {Common.TruckConfig} [options.truckConfig] Default: <code>TrimbleMaps.Common.TruckConfig.NONE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> <br> The vehicle style. <ul><li><code>TrimbleMaps.Common.TruckConfig.NONE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Indicates that no particular vehicle style is chosen.</li><li><code>TrimbleMaps.Common.TruckConfig.TWENTY_EIGHT</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - 28' double trailer.</li><li><code>TrimbleMaps.Common.TruckConfig.FORTY</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - 40' straight trailer.</li><li><code>TrimbleMaps.Common.TruckConfig.FORTY_EIGHT</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - 48' semitrailer.</li><li><code>TrimbleMaps.Common.TruckConfig.FIFTY_THREE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - 53' semitrailer.</li></ul>
 * @param {boolean} [options.inclTollData] Default: <code>true</code> <br> Indicates whether or not reports should calculate toll costs in reports.
 * @param {number} [options.fuelEconLoad] The fuel efficiency (distance units per fuel units) when loaded. Used for reports.
 * @param {number} [options.fuelEconEmpty] The fuel efficiency (distance units per fuel units) when empty. Used for reports.
 * @param {number} [options.costPerFuelUnit] The cost per fuel unit of fuel. Used for reports.
 * @param {number} [options.costGHG] The cost of greenhouse gas in lbs. per fuel unit. Used for reports
 * @param {number} [options.costMaintLoad] The maintenance cost per distance unit when loaded. Used for reports.
 * @param {number} [options.costMaintEmpty] The maintenance cost per distance unit when empty. Used for reports.
 * @param {number} [options.costTimeLoad] The time cost per mile/km when loaded. Used for reports.
 * @param {number} [options.costTimeEmpty] The time cost per mile/km when empty. Used for reports.
 * @param {Common.TollCurrency} [options.tollCurrency] Default: <code>TrimbleMaps.Common.TollCurrency.USD</code><span class="copy-to-clipboard" title="Copy to clipboard"></span>
 * @param {number} [options.exchangeRate] The change rate to convert US dollars to Canadian dollars. Used for reports.
 * @param {string} [options.tollPlan] Toll discount program. This field is only valid if the user has quarterly toll data privileges and the inclTollData (UseTollData) is set to true.
 * @param {boolean} [options.custRdSpeeds] Indicating whether or not to use custom road speeds.
 * @param {boolean} [options.useEUTollsReport] Indicating whether EU toll report should be reported.
 * @param {boolean} [options.useNonNaTolls] Indicating whether SA toll report should be reported.
 * @param {boolean} [options.includeRoadClass] Indicating whether the RoadClass will be included in the report.
 * @param {number} [options.citeInt] The cite interval to be used when generating a geotunnel report. The minimum value is 0.1. Used for reports.
 * @param {Common.Region} [options.region] Default: <code>TrimbleMaps.Common.Region.NA</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> <br> The data region in which the route stops are located. <ul><li><code>TrimbleMaps.Common.Region.AF</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Africa</li><li><code>TrimbleMaps.Common.Region.AS</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Asia</li><li><code>TrimbleMaps.Common.Region.EU</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Europe</li><li><code>TrimbleMaps.Common.Region.ME</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Middle East</li><li><code>TrimbleMaps.Common.Region.NA</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - North America</li><li><code>TrimbleMaps.Common.Region.OC</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Australia</li><li><code>TrimbleMaps.Common.Region.SA</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - South America</li><li><code>TrimbleMaps.Common.Region.WW</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Worldwide</li></ul>
 * @param {Common.DataVersion} [options.dataVersion]
 * @param {Array} [options.reportType] Default: <code>[TrimbleMaps.Common.ReportType.MILEAGE]</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> <br> The data region in which the route stops are located. <ul><li><code>TrimbleMaps.Common.ReportType.MILEAGE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Mileage</li><li><code>TrimbleMaps.Common.ReportType.DIRECTIONS</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Directions</li><li><code>TrimbleMaps.Common.ReportType.DETAIL</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Detail</li><li><code>TrimbleMaps.Common.ReportType.STATE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - State</li><li><code>TrimbleMaps.Common.ReportType.TOLL</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Toll Detail</li><li><code>TrimbleMaps.Common.ReportType.ROAD</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Road Type</li></ul>
 * @param {number} [options.elevLimit] Elevation limit when generating a route
 * @param {boolean} [options.hosEnabled] Gets or sets whether HoursOfService is enabled.
 * @param {number} [options.hosRemDriveTimeUntilBreak] Gets or sets the available driving hours at origin before the driver needs to take a rest break. The format is in seconds.
 * @param {number} [options.hosRemDriveTime] Gets or sets the available driving hours at origin until the end of work day. The format is in seconds.
 * @param {number} [options.hosRemOnDutyTime] Gets or sets the available On-Duty hours available at origin until the end of work day. The format is in seconds.
 * @param {Common.HoSScheduleType} [options.hosRuleType] Gets or sets the hours of service rule currently in use.
 * @param {number} [options.hosRemCycleDutyTime] Gets or sets the available Cycle duty time at origin until Cycle reset.  The format is in seconds.
 *
 * @param {Array<number>} [options.hosCycleTimeGains] The list of cycle time gains per day. The format is in seconds.
 * @param {string} [options.hosLastCycleResetDateTime] The last cycle reset for Canadian hours of service
 * @param {string} [options.hosLast24HourBreakDateTime] The date and time of the last off duty break that was at least 24 consecutive hours for Canadian hours of service.
 * @param {number} [options.hosRemainingCanadaOnDutyTime] The available On-Duty hours available at origin until the end of work day. The format is in seconds.
 * @param {number} [options.hosRemainingDriveTimeUntilBreakDriver2] The available driving hours at origin before the driver needs to take a rest break. The format is in seconds. Used only in Team Driving.
 * @param {number} [options.hosRemainingDriveTimeDriver2] The available driving hours at origin until the end of work day. The format is in seconds. Used only in Team Driving.
 * @param {number} [options.hosRemainingOnDutyTimeDriver2] The available On-Duty hours available at origin until the end of work day. The format is in seconds. Used only in Team Driving.
 * @param {number} [options.hosRemainingCycleDutyTimeDriver2] The available Cycle duty time at origin until Cycle reset. The format is in seconds. Used only in Team Driving.
 * @param {Array<number>} [options.hosCycleTimeGainsDriver2] The list of cycle time gains per day. The format is in seconds. Used only in Team Driving.
 * @param {string} [options.hosLastCycleResetDateTimeDriver2] The time of the last cycle reset for Canadian hours of service. Used only in Team Driving.
 * @param {string} [options.hosLast24HourBreakDateTimeDriver2] The date and time of the last off duty break that was at least 24 consecutive hours for Canadian hours of service. Used only in Team Driving.
 * @param {number} [options.hosRemainingCanadaOnDutyTimeDriver2] The available On-Duty hours available at origin until the end of work day. The format is in seconds. Used only in Team Driving.
 * @param {number} [options.hosAlternateHoSRuleType] The alternate Hours of Service rule type to use if crossing international borders between the US and Canada.
 * @param {number} [options.hosRemainingTimeUntilFullDayBreak] The available On-Duty hours available at origin until the driver must take a 24 hour break.
 * @param {number} [options.hosRemainingTimeUntilFullDayBreakDriver2] The available On-Duty hours available for Driver 2 at origin until the driver must take a 24 hour break. Used for Canada Cycle Two only.  The format is in seconds.
 * @param {number} [options.hosRemainingDrivingTimeInWeek] The available Driving hours available at origin until the driver must a break. Used for Europe only.  The format is in seconds.
 * @param {number} [options.hosRemainingOnDutyTimeInDay] The available On Duty Time In Day hours available at origin until the driver must a break. Used for Europe only.  The format is in seconds.
 * @param {number} [options.hosRemainingConsecutiveDrivingTime] The available Consecutive Driving hours available at origin until the driver must a break. Used for Europe only.  The format is in seconds.
 * @param {number} [options.hosRemainingFortnightlyTime] The available Fortnightly hours available at origin until the driver must a break. Used for Europe only.  The format is in seconds.
 * @param {number} [options.hosCurrentOffDutyTime] The rest time already taken at a stop. Used for NA and Europe, regular and team driving.  The format is in seconds.
 * @param {number} [options.hosCurrentOffDutyTimeDriver2] The rest time already taken at a stop. Used for NA and Europe, regular and team driving.  The format is in seconds.
 * @param {boolean} [options.hosUseCycleReset] Whether we do full cycle time reset or not.
 * @param {string} [options.hosTerminalDayStartTime] The time of day used to determine when a 24-hour period defining a 'day' starts. The format should be "HH:mm:sszzz"
 * @param {boolean} [options.hosUseCustomPlaces] Whether to use custom places for HOS rest stops.
 * @param {boolean} [options.hosTeamDriving] Whether there are team drivers.
 *
 * @param {number} [options.hosShortBreakDuration] The short break duration. The format is in seconds. CustomBreakDurations
 * @param {number} [options.hosEndOfDutyBreakDuration] The end of duty break duration. The format is in seconds. CustomBreakDurations
 * @param {number} [options.hosCycleResetBreakDuration] The cycle reset break duration. The format is in seconds. CustomBreakDurations
 * @param {number} [options.hosDriverSwitchBreakDuration] The driver switch break duration. The format is in seconds. CustomBreakDurations
 * @param {number} [options.hosBorderCrossingWaitDuration] The time spent waiting at border crossings. The format is in seconds. CustomBreakDurations
 * @param {number} [options.hosBreakTimeInDayDuration] The break time in day duration. The format is in seconds. Used for HOS Europe only. CustomBreakDurations
 * @param {number} [options.hosBreakTimeInWeekDuration] The break time in week duration. The format is in seconds. Used for HOS Europe only. CustomBreakDurations
 *
 * @param {number} [options.hosMaxAlternateRestStops] Whether the maximum number of Alternate Rest Stops to be displayed in the report.  Defaults to 0.
 * @param {boolean} [options.hosSkipRestStopInsertion] Whether to skip inserting rest stops in the route
 * @param {number} [options.hosActiveDriver] Whether the active driver at the beginning of the trip. Used for Team Driving.
 * @param {number} [options.hosMaxDrivingTimeInDay] The maximum driving time allowed in a day. Used for HOS Europe only
 * @param {number} [options.hosMaxOnDutyTimeInDay] The maximum On-Duty time allowed in a day. Used for HOS Europe only
 * @param {number} [options.hosMaxOnDutyTimeInWeek] The maximum On-Duty time allowed in a week. Used for HOS Europe only
 * @param {boolean} [options.hosAdverseConditionException] a value indicating where adverse driving conditions are present.  If so, consecutive driving an daily driving. HOS clock are extended by 2 hours due to the adverse driving condition exception.
 * @param {boolean} [options.hosPOIStopsReturned] A value indicating whether POI Rest Stops will be returned, or only Lat/Lon Rest Stops will be returned.
 * @param {number} [options.hosHOSWindowStart] The start time of the window during the trip may insert an HOS Rest Stop
 * @param {number} [options.hosHOSWindowEnd] The end time of the window during the trip may insert an HOS Rest Stop
 *
 * @param {boolean} [options.maintainHeading] When this option is set, a vehicle will attempt to avoid u-turns at intermediate stops and instead leave the stop in the same direction it entered it. When using the school bus vehicle profiles, this option will be automatically set to true if not supplied.
 * @param {Common.SideOfStreetAdherenceLevel} [options.sideOfStreetAdherence] Represents the levels of adherence that apply a cost to a route if its destination is on the opposite side of the street.
 * @param {number} [options.governorSpeedLimit] Maximum average road speed to use in route calculations that overrides all other road speeds when they are above this value.
 * @param {boolean} [options.useAvoidsAndFavors] Indicating whether or not to use avoids and favors
 * @param {boolean} [options.useSites] Default: <code>false</code>
 * @param {Common.TruckConfig} [options.vehicleProfile] Provides the ability to load a vehicle profile which includes vehicle dimensions, route costs and routing options for a specific vehicle class. This option replaces the vehicleType and SharedOptions.truckStyle.
 * @param {boolean} [options.refrigerated] Indicates whether the vehicle is a refrigerated truck.
 * @param {Common.FuelType} [options.fuelType] Fuel type
 * @param {number} [options.fuelConsumption] Indicates the fuel consumption of the vehicle.
 * @param {number} [options.trafficLookaheadDistance] Distance in miles for which we will gather traffic for the route.
 * @param {Array<string>} [options.avoidState] A list of states are avoided, for example ['NJ', 'PA']. ISO2 code.
 * @param {Array<string>} [options.avoidCountry] A list of countries are avoided, for example ['US', 'CA']. ISO2 code.
 * @param {Array<string>} [options.favorState] A list of states are in favor.
 * @param {Array<string>} [options.favorCountry] A list of countries are in favor.
 * @param {boolean} [options.includeTrimblePlaceIDs] Indicates use Trimble placeIds from while the processing route
 * @param {boolean} [options.useCrossStreetTurnAvoidance] Turns avoid cross street turns logic on or off.
 * @param {boolean} [options.congestionZonesOpen] ?
 * @param {boolean} [options.lowEmissionZonesOpen] ?
 * @param {Common.DisplayRestrictionsType} [options.displayRestrictions] ?
 * @param {string} [options.profileName] Profile name
 * @param {string} [options.profileId] Profile Id
 * @param {boolean} [options.isDraggable] Default: <code>false</code>
 * @param {DragRouteOptions} [options.dragOptions] Options for customizing route dragging behavior, such as snapping the route path to a nearby road link that is visible. By default, with dragging enabled, you can activate this snapping behavior by holding the SHIFT key while dragging. <br>By adding a valid viaPointModifierKey property value, you can enable removing stops that are introduced by drag routing. It is better either enabling undo-redo feature or the removing stop feature. It is not recommended to use both features at the same time. Also be ware, update route function only takes LngLat or LngLatLike in an array, flags that indicating stops are generated by drag routing will be removed. After route update, no stops can be removed. After this option is set, it can't be changed such as using the update method.
 * @param {string} [options.assetId] The ID of the asset (device, vehicle or driver) associated.
 * @param {string} [options.estimatedTimeOpts] Estimated time options allow you to specify either a day of the week or a specific date and the time for which you would like you depart from your origin or arrive at your destination.
 * @param {boolean} [options.useTraffic] Default: <code>false</code> <br> Indicates whether or not use traffic which is only used in conjunction with fastest routing.
 * @param {boolean} [options.includeVehicleRestrictedCleanupPoints] Boolean that designates if the customer wants the vehicle restricted cleanups to be separated from the main coordinate list and returned separately.
 * @param {Common.TrailerType} [options.trlType] Default: <code>TrimbleMaps.Common.TrailerType.NONE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> <br> Type of the trailer. For Toll Detail reports in Europe. <ul><li><code>TrimbleMaps.Common.TrailerType.NONE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - None</li><li><code>TrimbleMaps.Common.TrailerType.CARAVAN</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Caravan</li><li><code>TrimbleMaps.Common.TrailerType.TRAILER</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Trailer</li></ul>
 * @param {number} [options.trlCount] Default: <code>0</code><br> Number of trailers and semi-trailers. For Toll Detail reports in Europe.
 * @param {string} [options.trlMaxHt] Max height of the trailer in feet and inches, or meters depending on units. For Toll Detail reports in Europe.
 * @param {number} [options.trlMaxWt] Max weight of the trailer in pounds or kilograms depending on units. For Toll Detail reports in Europe.
 * @param {number} [options.trlMaxAxles] Default: <code>1</code><br> Max number of axles on the trailer. Acceptable values are 1 - 8. For Toll Detail reports in Europe.
 * @param {boolean} [options.isHybrid] Default: <code>false</code><br> Indicates whether vehicle has a hybrid engine. For Toll Detail reports in Europe.
 * @param {Common.EmissionType} [options.emiType] Default: <code>TrimbleMaps.Common.EmissionType.EUROVI</code> <span class="copy-to-clipboard" title="Copy to clipboard"></span> <br>The emission type of the engine. For Toll Detail reports in Europe.<ul><li><code>TrimbleMaps.Common.EmissionType.EURO0</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - Euro0</li><li><code>TrimbleMaps.Common.EmissionType.EUROI</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - EuroI</li><li><code>TrimbleMaps.Common.EmissionType.EUROII</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - EuroII</li><li><code>TrimbleMaps.Common.EmissionType.EUROIII</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - EuroIII</li><li><code>TrimbleMaps.Common.EmissionType.EUROIV</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - EuroIV</li><li><code>TrimbleMaps.Common.EmissionType.EUROV</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - EuroV</li><li><code>TrimbleMaps.Common.EmissionType.EUROVI</code><span class="copy-to-clipboard" title="Copy to clipboard"></span>EuroVI</li><li><code>TrimbleMaps.Common.EmissionType.EUROEEV</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - EuroEEV</li></ul>
 * @param {boolean} [options.minPoll] Default: <code>false</code><br> Indicates whether the vehicle is a minimum vehicle pollution. For Toll Detail reports in Europe.
 * @param {boolean} [options.frameRoute] Default: <code>undefined</code><br> Indicates whether the center point and zoom level of the map will be adjusted to show the route in full frame. Only when it sets to false, the route will not be framed.
 * @param {boolean} [options.showStops] Default: <code>undefined</code><br> Indicates whether the stops are displayed. After it is set, it can't be changed. When it sets to false, 1) stops will not be displayed, 2) originIcon, destinationIcon, and stopIcon will be ignored, 3) update function can be used to change route color, 4) it allows user to display custom icons, see examples on the Developer Portal.
 * @param {Common.ZoneType} [options.congestionZone] Congestion zone value
 * @param {Common.ZoneType} [options.lowEmissionZone] Low emission zone value
 * @param {Common.ZoneType} [options.ultraLowEmissionZone] Ultra low emission zone value
 * @param {Common.ClassOverride} [options.overrideClass] Default: <code>TrimbleMaps.Common.ClassOverride.NONE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> <br> Represents type of class overrides. <ul><li><code>TrimbleMaps.Common.ClassOverride.NONE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - No class overrides</li><li><code>TrimbleMaps.Common.ClassOverride.FIFTY_THREE</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - 53' class override</li><li><code>TrimbleMaps.Common.ClassOverride.NATIONAL_NETWORK</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - National Network class override</li><li><code>TrimbleMaps.Common.ClassOverride.ALL</code><span class="copy-to-clipboard" title="Copy to clipboard"></span> - 53' and National Network class override</li></ul>
 * @param {Common.RequestMethod} [options.requestMethod] Default: <code>undefined</code>. Uses HTTP GET method as default. Set the request method to `POST` to use HTTP POST method. Not every route option is supported by 'GET' method.
 * @param {Object} [options.frameOptions] A {@link Map#fitBounds} options object used to frame the route on the map when frameRoute is set to true (to be precise, not false). Default is 50px padding. After this option is set, it can't be changed such as using the update method.
 * @param {boolean} [options.showArrows] Default <code>false</code> Indicates whether direction arrows are drawn on top of the route line.
 * @param {ArrowOptions} [options.arrowOptions] Options for customizing the vizualization of route arrows.
 * @param {boolean} [options.isVisible] Default: <code>true</code> Indicates whether the route will be visible when added to the map.
 * @param {boolean} [options.hubRouting] Default: <code>false</code> Indicates whether or not to enable hub routing. Set to true to enable hub routing. When hub routing is enabled, drag routing and route optimizaion will be disabled.
 * @param {boolean} [options.styleChangeFrameRoute] Default: <code>undefined</code> If it is set to true or false, <code>options.frameRoute</code> will be ignored temporarily and route will or will not be framed when style changes. If not set or set to null, <code>options.frameRoute</code> will be used.
 *
 * @example
 * var route = new TrimbleMaps.Route({
 *     routeId: 'myRoute',
 *     routeWidth: 8, // or {stops: [[10, 7],[15, 9],[19, 11]]}
 *     stops: [
 *         new TrimbleMaps.LngLat(-74.566234, 40.499440),
 *         new TrimbleMaps.LngLat(-74.629749, 40.261187)
 *     ],
 *     routeType: TrimbleMaps.Common.RouteType.SHORTEST,
 *     frameRoute: false,
 *     showStops: false,
 *     overrideClass: TrimbleMaps.Common.ClassOverride.FIFTY_THREE,
 *     requestMethod: TrimbleMaps.Common.RequestMethod.POST
 *   })
 *   .addTo(map);
 */
export default class Route extends Evented {
    _routeId: string;
    _routeColor: string;
    _routePathOpacity: number;
    _routeWidth: RouteWidth | number;

    _routeLayerName: string;
    _stopLayerName: string;

    _routeSourceName: string;
    _stopSourceName: string;

    _startIconName: string;
    _endIconName: string;
    _stopIconName: string;

    _startIcon: string;
    _endIcon: string;
    _stopIcon: string;

    _map: Map;
    _routeSourceData: ?Object;
    _stopSourceData: ?Object;
    _options: Object;
    _routeLegPositions: Array<number>;

    _originIconOptions: IconOptions;
    _destinationIconOptions: IconOptions;
    _stopIconOptions: IconOptions;
    _showStops: boolean;

    _showArrows: boolean;
    _arrowOptions: ArrowOptions;

    _dragRouteHandler: DragRouteHandler;
    _undoRedoStack: ?UndoRedoStack;

    _hoverCount: number;
    _isVisible: boolean;
    _arrowLayer: RouteArrow;

    constructor(options: RouteOptions) {
        if (!config.API_KEY) throw Error('TrimbleMaps.APIKey is not set');
        super();

        const extendedOptions = extend({
            frameOptions: {
                padding: {top: 50, bottom: 50, left: 50, right: 50}
            }
        }, options);
        this._options = this.getConvertedUnitValue(extendedOptions);
        this._routeLegPositions = [];
        this._routeId = this._generateRouteId(this._options);
        this._showStops = this._options.showStops !== false;
        this._showArrows = this._options.showArrows === true;
        this._isVisible = this._options.isVisible !== false;

        this._routeColor = (this._options.routeColor) ? this._options.routeColor : '#123EC4';
        this._routePathOpacity = (this._options.routePathOpacity === 0 || (this._options.routePathOpacity)) ?
            this._options.routePathOpacity :
            0.6;
        this._routeWidth = (this._options.routeWidth) ? this._options.routeWidth : defaultRouteWidth;

        this._routeLayerName = `route-layer-${this._routeId}`;
        this._stopLayerName = `stop-layer-${this._routeId}`;

        this._routeSourceName = `route-source-${this._routeId}`;
        this._stopSourceName = `stop-source-${this._routeId}`;

        this._startIconName = `start-icon-${this._routeId}`;
        this._endIconName = `end-icon-${this._routeId}`;
        this._stopIconName = `stop-icon-${this._routeId}`;

        if (this._showStops === true) {
            this._originIconOptions = extend({}, defaultOriginIconOptions, this._options.originIcon);
            this._destinationIconOptions = extend({}, defaultDestIconOptions, this._options.destinationIcon);
            defaultStopIconOptions.url = createStopIcon(this._routeColor); // make sure stops and route path have the same color.
            this._stopIconOptions = extend({}, defaultStopIconOptions, this._options.stopIcon);
        }

        this._routeSourceData = null;
        this._stopSourceData = null; // geoJson with properties of stopType, stopLabel, and isViaPoint (is used to flag a stop that is generated by drag routing)

        //we need stops to run the route
        if (this._options && this._options.stops) {
            this._getRoute(this._options);
        }
        // fix the issue that dragOptions is defined but never used.
        this._options.dragOptions = extend({}, defaultDragRouteOptions, this._options.dragOptions);
        this._dragRouteHandler = new DragRouteHandler(this);
        this._undoRedoStack = null;

        bindAll([
            '_onMouseClick',
            '_onMouseEnter',
            '_onMouseLeave',
            '_onStyleChange'
        ], this);

        this._hoverCount = 0;
    }

    getConvertedUnitValue(options: RouteOptions): RouteOptions {
        if (config !== null && config !== undefined && typeof (config) !== undefined) {
            if (config.UNIT !== null && config.UNIT !== undefined) {
                options.distanceUnits = config.UNIT === Common.DistanceUnit.KILOMETERS ? Common.DistanceUnit.KILOMETERS :  Common.DistanceUnit.MILES;
                options.trkUnits = config.UNIT === Common.Unit.METRIC ? Common.TruckUnit.METRIC :  Common.TruckUnit.ENGLISH;
            } else {
                options.trkUnits = options.distanceUnits === Common.DistanceUnit.KILOMETERS ? Common.TruckUnit.METRIC : Common.TruckUnit.ENGLISH;
            }
        }
        Object.keys(options).map((key) => {
            if (typeof (options[key]) !== undefined && typeof (options[key]) === 'number' && options[key] > 0 && options[key] !== undefined && options[key] !== null && options.distanceUnits !== null && options.distanceUnits !== undefined) {
                if (key === 'trkLength') {
                    options[key] = convertToSelectedUnit(true, options[key], 'length', options.distanceUnits);
                } else if (key === 'trkWidth') {
                    options[key] = convertToSelectedUnit(true, options[key], 'width', options.distanceUnits);
                } else if (key === 'trkHeight' || key === 'trlMaxHt') {
                    options[key] = convertToSelectedUnit(true, options[key], 'height', options.distanceUnits);
                } else if (key === 'trkWeight' || key === 'trlMaxWt') {
                    options[key] = convertToSelectedUnit(true, options[key], key === 'trkWeight' ? 'weight' : 'maxWeight', options.distanceUnits);
                }
            }
            return options;
        });
        return options;
    }

    _getLayerNames(includeArrowLayer: boolean): string[] {
        const layerNames = [this._routeLayerName, this._stopLayerName];
        if (includeArrowLayer === true && typeof this._arrowLayer !== 'undefined') {
            layerNames.splice(1, 0, this._arrowLayer.getName());
        }
        return layerNames;
    }

    /**
     * Set the layer visibility.
     * @param {boolean} isVisible A boolean indicating the visibility of the layer.
     * @returns {Route} `this`
     */
    setVisibility(isVisible: boolean): Route {
        this._isVisible = isVisible;
        if (this._map) {
            const layerNames = this._getLayerNames(false);
            const routeLayer = this._map.getLayer(layerNames[0]);
            if (routeLayer) {
                const layerVisibility = this._map.getLayoutProperty(layerNames[0], 'visibility');
                const visibility = (isVisible) ? 'visible' : 'none';
                if (visibility !== layerVisibility) {
                    layerNames.forEach((l) => {
                        if (this._map.getLayer(l)) {
                            this._map.setLayoutProperty(l, 'visibility', visibility);
                        }
                    });
                    if (typeof this._arrowLayer !== 'undefined') {
                        this._arrowLayer.setVisibility(isVisible);
                    }
                    // this._map._update(true); // Without this line, it still works.
                    /**
                     * Fired when layer visibility changes.
                     *
                     * @event visibility
                     * @memberof Map
                     * @instance
                     * @type {Object}
                     * @property {Event} route visibility has been changed.
                     */
                    this.fire(new Event('visibility'));
                }
            }
        }
        return this;
    }

    /**
     * Set the active route opacity 0.6 and enable the drag options and vice versa.
     * @param {boolean} isActive A boolean indicating the route is currently visible on the map.
     * @returns {Route} `this`
     */
    setRouteState(isActive: boolean): Route {
        if (this._map) {
            const opacity = isActive ? 0.6 : 0.3;
            this._updateRoutePathOpacity(opacity);
            this.setDraggable(isActive);
        }
        return this;
    }

    /**
     * Returns true if the layer is enabled.
     * @returns {boolean} A boolean indicating the visibility of the layer.
     */
    isVisible(): boolean {
        return this._isVisible;
    }

    /**
     * Toggle layer visibility.
     * @returns {Route} `this`
     */
    toggleVisibility(): Route {
        return this.setVisibility(!this.isVisible());
    }

    /**
     * Attaches the route to a map
     * @param {Map} map The map used for displaying the route.
     * @returns {Route} `this`
     */
    addTo(map: Map) {
        if (this._isUnique(map)) {
            this.remove();
            this._map = map;
            this._arrowLayer = new RouteArrow(this);
            map.on('style.load', this._onStyleChange);

            this._map.on('click', this._routeLayerName, this._onMouseClick);
            this._map.on('click', this._stopLayerName, this._onMouseClick);
            this._map.on('mouseenter', this._routeLayerName, this._onMouseEnter);
            this._map.on('mouseenter', this._stopLayerName, this._onMouseEnter);
            this._map.on('mouseleave', this._routeLayerName, this._onMouseLeave);
            this._map.on('mouseleave', this._stopLayerName, this._onMouseLeave);

            this._updateSources();
            this._initMap(map);
        } else {
            throw new Error(`A route with the routeId of ${this._routeId} already exists on this map.`);
        }

        return this;
    }

    /**
     * Gets the bottom layer id for this route.
     *
     * @returns {string} layer id or undefined
     */
    getBottomLayerId(): ?string {
        let bottomLayerId;
        if (this._map) {
            const layerNames = this._getLayerNames(true);
            const order = this._map.style._order;
            const indexes = [];
            for (let i = 0; i < layerNames.length; i++) {
                if (this._map.getLayer(layerNames[i])) {
                    const idx = order.lastIndexOf(layerNames[i]);
                    indexes.push(idx);
                }
            }
            if (indexes.length > 0) {
                const minIndex = Math.min(...indexes);
                if (minIndex > -1) {
                    bottomLayerId = order[minIndex];
                }
            }
        }
        return bottomLayerId;
    }

    /**
     * Moves route layers to a different z-position. The route will be inserted before the layer with
     * ID `beforeId`, or appended on the top of the map if `beforeId` is omitted.
     * @param {string} [beforeId] Optional ID of an existing layer to insert before
     * @returns {Route} `this`
     *
     * @example
     * const myRoute1 = new TrimbleMaps.Route();
     * myRoute1.addTo(map);
     * myRoute1.moveLayer('layerId');
     */
    moveLayer(beforeId?: string) {
        if (this._map) {
            if (this._routeId === beforeId) {
                return;
            }
            const layerNames = this._getLayerNames(true);
            for (let i = 0; i < layerNames.length; i++) {
                if (this._map.getLayer(layerNames[i])) {
                    this._map.moveLayer(layerNames[i], beforeId);
                }
            }
        }
        return this;
    }

    /**
     * Removes the route from a map
     * @example
     * var route = new TrimbleMaps.Route().addTo(map);
     * route.remove();
     * @returns {Route} `this`
     */
    remove() {
        if (this._map) {
            //remove layers
            const layerNames = this._getLayerNames(false);
            // remove route and stop layers
            layerNames.forEach((name) => {
                if (this._map.getLayer(name)) this._map.removeLayer(name);
            });
            // remove arrow layer
            if (typeof this._arrowLayer !== 'undefined') this._arrowLayer.removeLayer();
            //remove sources
            if (this._map.getSource(this._routeSourceName)) this._map.removeSource(this._routeSourceName);
            if (this._map.getSource(this._stopSourceName)) this._map.removeSource(this._stopSourceName);
            //remove stop images
            if (this._map.hasImage(this._startIconName)) this._map.removeImage(this._startIconName);
            if (this._map.hasImage(this._endIconName)) this._map.removeImage(this._endIconName);
            if (this._map.hasImage(this._stopIconName)) this._map.removeImage(this._stopIconName);

            this._map.off('style.load', this._onStyleChange);

            this._map.off('click', this._routeLayerName, this._onMouseClick);
            this._map.off('click', this._stopLayerName, this._onMouseClick);
            this._map.off('mouseenter', this._routeLayerName, this._onMouseEnter);
            this._map.off('mouseenter', this._stopLayerName, this._onMouseEnter);
            this._map.off('mouseleave', this._routeLayerName, this._onMouseLeave);
            this._map.off('mouseleave', this._stopLayerName, this._onMouseLeave);

            //remove drag handler
            this._dragRouteHandler.disable();
            delete this._map;
            delete this._arrowLayer;
            this._routeSourceData = null;
            this._stopSourceData = null;
        }

        return this;
    }

    /**
     * Updates the route. It particularly updates route path, stop icons, route color, route opacity, route width, route arrow, drag routing. If more controlling over route stops styling is desirable, instead of using this function, construct a route with showStops setting to false, then adding custom stops icons uses addStopIcon function and styling them uses styleStopIcons function.
     * @param {RouteOptions} options The route options to be updated.
     */
    update(options: RouteOptions) {
        if (this._map && options) {
            this._options = extend(this._options, options);
            this._getRoute(this._options);
            if (this._showStops === true) {
                if ((options.stopIcon === undefined || options.stopIcon.url === undefined) &&
                    (options.routeColor && (options.routeColor !== this._routeColor))) {
                    this._stopIcon = createStopIcon(options.routeColor);
                    this._loadIcon(this._stopIconName, this._stopIcon, defaultStopIconOptions.url);
                }
            }

            if (options.routeColor && (options.routeColor !== this._routeColor)) {
                this._updateRouteColor(options.routeColor);
            }

            if ((options.routePathOpacity === 0 || (options.routePathOpacity)) &&
                (options.routePathOpacity !== this._routePathOpacity)) {
                this._updateRoutePathOpacity(options.routePathOpacity);
            }

            if (options.routeWidth && (options.routeWidth !== this._routeWidth)) {
                this._updateRouteWidth(options.routeWidth);
            }

            if (this._showStops === true) {
                if (options.originIcon) {
                    this._originIconOptions = extend({}, defaultOriginIconOptions, options.originIcon);
                    if (options.originIcon && options.originIcon.url !== undefined) {
                        this._loadIcon(this._startIconName, this._originIconOptions.url, defaultOriginIconOptions.url);
                    }
                    this._originIconOptions = extend({}, this._originIconOptions, options.originIcon);
                    if (this._originIconOptions.offset !== defaultOriginIconOptions.offset) {
                        this._updateIconOffset();
                    }
                    if (this._originIconOptions.size !== defaultOriginIconOptions.size) {
                        this._updateIconSize();
                    }
                    if (this._originIconOptions.opacity !== defaultOriginIconOptions.opacity) {
                        this._updateIconOpacity();
                    }
                    if (this._originIconOptions.textOpacity !== defaultOriginIconOptions.textOpacity) {
                        this._updateTextOpacity();
                    }
                }
                if (options.destinationIcon) {
                    this._destinationIconOptions = extend({}, defaultDestIconOptions, options.destinationIcon);
                    if (options.destinationIcon && options.destinationIcon.url !== undefined) {
                        this._loadIcon(this._endIconName, this._destinationIconOptions.url, defaultDestIconOptions.url);
                    }
                    this._destinationIconOptions = extend({}, this._destinationIconOptions, options.destinationIcon);
                    if (this._destinationIconOptions.offset !== defaultDestIconOptions.offset) {
                        this._updateIconOffset();
                    }
                    if (this._destinationIconOptions.size !== defaultDestIconOptions.size) {
                        this._updateIconSize();
                    }
                    if (this._destinationIconOptions.opacity !== defaultDestIconOptions.opacity) {
                        this._updateIconOpacity();
                    }
                    if (this._destinationIconOptions.textOpacity !== defaultDestIconOptions.textOpacity) {
                        this._updateTextOpacity();
                    }
                }
                if (options.stopIcon) {
                    this._stopIconOptions = extend({}, defaultStopIconOptions, options.stopIcon);
                    if (options.stopIcon && options.stopIcon.url !== undefined) {
                        this._loadIcon(this._stopIconName, this._stopIconOptions.url, defaultStopIconOptions.url);
                    }
                    this._stopIconOptions = extend({}, this._stopIconOptions, options.stopIcon);
                    if (this._stopIconOptions.offset !== defaultStopIconOptions.offset) {
                        this._updateIconOffset();
                    }
                    if (this._stopIconOptions.size !== defaultStopIconOptions.size) {
                        this._updateIconSize();
                    }
                    if (this._stopIconOptions.opacity !== defaultStopIconOptions.opacity) {
                        this._updateIconOpacity();
                    }
                    if (this._stopIconOptions.textOpacity !== defaultStopIconOptions.textOpacity) {
                        this._updateTextOpacity();
                    }
                }
            }

            // showArrows changed
            if (this._showArrows !== this._options.showArrows) {
                this._showArrows = this._options.showArrows;

                if (this._showArrows) { // from no show to show, 3 cases: empty, partial, full
                    this._arrowLayer.addLayer(this._options.arrowOptions);
                } else {
                    // remove
                    this._arrowLayer.removeLayer();
                }
            } else {
                // update
                if (this._showArrows === true) {
                    // options.arrowOptions: undefined -> not change previous settings; null or {} -> delete previous settings using default.
                    this._arrowLayer.updateLayer(this._options.arrowOptions);
                }
            }

            this.setDraggable(this._options.isDraggable === true);
        }
    }

    /**
     * @deprecated
     * Sets the map view to fit the route.
     * @returns {Route} `this`
     */
    frameRoute() {
        return this.frame();
    }

    /**
     * Sets the map view to fit the route.
     * @returns {Route} `this`
     */
    frame() {
        if (this._routeSourceData) {
            const bounds = new LngLatBounds();
            this._routeSourceData.geometry.coordinates.forEach(segment => segment.forEach(coord => bounds.extend(coord)));
            if (!bounds.isEmpty()) {
                this._map.fitBounds(bounds, this._options.frameOptions);
            }
        }

        return this;
    }

    /**
     * Gets the Route ID
     * @returns {string} ID of the route
     */
    getRouteId() {
        return this._routeId;
    }

    /**
     * Gets reports for the route options
     * @param {RouteOptions} options The route options used for the reports.
     */
    getReports(options: RouteOptions) {
        this._options = extend(this._options, options);
        getRouteReports(this);
    }

    /**
     * Changes stop icons styles. Following properties are assigned internally: id, type, source, layout['icon-image'], layout['text-font']
     * @param {Object} style For detailed information, visit the <a href='https://developer.trimblemaps.com/maps-sdk/style-spec/layers/#symbol' target='_blank'>Developer Portal</a>
     */
    styleStopIcons(style: Object) {
        if (style.layout === undefined) {
            warnOnce(`'layout' is required.`);
        } else {
            // if the images haven't loaded yet, set to an empty image to avoid image missing warnings
            if (!this._map.hasImage(this._startIconName)) this._map.addImage(this._startIconName, {width: 0, height: 0, data: null});
            if (!this._map.hasImage(this._endIconName)) this._map.addImage(this._endIconName, {width: 0, height: 0, data: null});
            if (!this._map.hasImage(this._stopIconName)) this._map.addImage(this._stopIconName, {width: 0, height: 0, data: null});

            const yourStyle = extend({}, style);
            yourStyle.id = this._stopLayerName;
            yourStyle.type = 'symbol';
            yourStyle.source = this._stopSourceName;
            yourStyle.layout['icon-image'] = [
                'case',
                ['==', ['get', 'stopType'], 'start'], this._startIconName,
                ['==', ['get', 'stopType'], 'end'], this._endIconName,
                this._stopIconName
            ];
            yourStyle.layout['text-font'] = ['Roboto Medium'];
            if (this._map.getLayer(this._stopLayerName)) {
                this._map.removeLayer(this._stopLayerName);
            }
            this._map.addLayer(yourStyle);
        }
    }

    /**
     * Adds stop icon
     * @param {string} stopType Valid values are `'start'`, `'end'`, `'stop'`.
     * @param {string} url Icon URL
     */
    addStopIcon(stopType: string, url: string) {
        if (stopType === 'start') {
            this._loadIcon(this._startIconName, url, defaultOriginIconOptions.url);
        } else if (stopType === 'end') {
            this._loadIcon(this._endIconName, url, defaultDestIconOptions.url);
        } else if (stopType === 'stop') {
            this._loadIcon(this._stopIconName, url, defaultStopIconOptions.url);
        } else {
            warnOnce(`stopType has to be 'start', 'end', or 'stop'.`);
        }
    }

    _getStopStatusViaPointFlags() {
        const viaPoints = [];
        if (this._stopSourceData !== null) {
            // $FlowFixMe
            this._stopSourceData.features.forEach(feature => {
                const isViaPoint = feature.properties.isViaPoint;
                viaPoints.push(isViaPoint === true ? isViaPoint : false);
            });
        }
        return viaPoints;
    }

    /**
     * Inserts a stop to a route after a given route leg, then gets the route.
     * @param {LngLat} newStop A stop
     * @param {number} routeLegPosition The sequence number of a route leg.
     */
    getRouteWithNewStop(newStop: LngLat, routeLegPosition: number) {
        const findPosition = this._routeLegPositions.find(element => element === routeLegPosition);
        if (findPosition !== undefined) {
            const viaPoints = this._getStopStatusViaPointFlags();
            // Add the newstop
            // Although a via point flag will be added to the new stop in this._stopSourceData, the stop is treated as a regular stop for now when getting a route.
            if (this._options.stops.length > 0) {
                const startIndex = routeLegPosition + 1;
                this._options.stops.splice(startIndex, 0, newStop);
                viaPoints.splice(startIndex, 0, true);
                if (this._undoRedoStack !== undefined && this._undoRedoStack !== null) {
                    this._undoRedoStack.push([...this._options.stops]);
                }
            }
            this._getRoute(this._options, viaPoints);
        } else {
            warnOnce(`routeLegPosition (${routeLegPosition}) is not one of the numbers in this list [${this._routeLegPositions.toString()}].`);
        }
    }

    /**
     * Can undo
     * @returns {boolean} returns true if can undo, otherwise false
     */
    canUndo () {
        if (this._undoRedoStack !== undefined && this._undoRedoStack !== null) {
            return this._undoRedoStack.canUndo();
        }
        return false;
    }

    /**
     * Can redo
     * @returns {boolean} returns true if can redo, otherwise false
     */
    canRedo () {
        if (this._undoRedoStack !== undefined && this._undoRedoStack !== null) {
            return this._undoRedoStack.canRedo();
        }
        return false;
    }

    /**
     * Undo route drag
     */
    undo () {
        if (this._undoRedoStack !== undefined && this._undoRedoStack !== null) {
            const stops = this._undoRedoStack.undo();
            this._updateStopsThenGetRoute(stops);
        }
    }

    /**
     * Redo route drag
     */
    redo () {
        if (this._undoRedoStack !== undefined && this._undoRedoStack !== null) {
            const stops = this._undoRedoStack.redo();
            this._updateStopsThenGetRoute(stops);
        }
    }

    /**
     * Gets route after updating stops.
     * @param {Array<Object>} stops Route stops
     */
    _updateStopsThenGetRoute(stops: ?Array<Object>) {
        if (stops !== undefined && stops !== null) {
            this._options.stops = [...stops];
            this._getRoute(this._options);
        }
    }

    /**
     * Enables route undo, redo stack. It is better either enabling undo-redo feature or the removing stop feature. It is not recommended to use both features at the same time.
     */
    enableRouteStack() {
        // add route stops to a new stack as the baseline.
        this._undoRedoStack = new UndoRedoStack({item: [...this._options.stops]});
    }

    /**
     * Disables route undo, redo stack.
     */
    disableRouteStack() {
        // remove items in the stack.
        this._undoRedoStack = null;
    }

    _addIconToMap(iconName: string, icon: any) {
        if (this._map) {
            if (this._map.hasImage(iconName)) {
                this._map.removeImage(iconName);
            }

            this._map.addImage(iconName, icon);

            // refresh source to make sure an empty image isn't cached for
            // the current zoom level (MAPS-2108)
            this._updateStopSource();
        }

    }

    /**
     * Origin, destination, stop icon images
     *
     * @param {string} iconName Image name to be used when adding to the map
     * @param {string} iconUrl URL or SVG string of the icon
     * @param {string} defaultIcon Default SVG string to be used if the icon doesn't load
     * @private
     */
    _loadIcon(iconName: string, iconUrl: string, defaultIcon: string, width?: number, height?: number): Promise<any> {
        const w = width || 150;
        const h = height || 150;
        return new Promise((resolve) => {
            if (iconUrl.toLocaleLowerCase().startsWith('http')) { // image URL
                // load image
                this._map.loadImage(iconUrl, (error, image) => {
                    if (error) {
                        warnOnce(`Unable to load image due to ${error}. Using default icon for ${iconName}.`);
                        loadSVGImage(defaultIcon, w, h).then((image) => {
                            // add to map
                            this._addIconToMap(iconName, image);
                            resolve();
                        });
                    } else {
                        // add to map
                        this._addIconToMap(iconName, image);
                        resolve();
                    }
                });
            } else if (iconUrl.indexOf('svg') > -1) { // SVG string
                // generate image element
                loadSVGImage(iconUrl, w, h).then((image) => {
                    // add to map
                    this._addIconToMap(iconName, image);
                    resolve();
                });
            } else {
                throw new Error(`Invalid image URL`);
            }

        });
    }

    _isUnique(map: Map) {
        if (map.getLayer(this._routeLayerName)) return false;
        return true;
    }

    _generateRouteId(options?: RouteOptions) {
        let id = '';
        if (options && options.routeId) {
            id = options.routeId;
        } else {
            id = Math.random().toString(36).substr(2, 7);
        }
        return id;
    }

    _initMap(map: Map) {
        const vis = (this._isVisible) ? 'visible' : 'none';

        if (this._options.isDraggable) {
            this.setDraggable(this._options.isDraggable);
        }
        //check for route layer
        if (map.getLayer(this._routeLayerName)) {
            warnOnce('Route Layer exists');
        } else {
            // adding route colors from content style
            if (this._map._routLineLayers.length) {
                const routeNumberToFetch =  Object.keys(this._map.style._layers).filter(str => str.includes('route-layer')).length;
                // $FlowFixMe
                const routeColorFromContentStyle =  routeNumberToFetch < this._map._routLineLayers.length ? this._map._routLineLayers.find(rl => rl.id === `route_${routeNumberToFetch}`).paint['line-color'] : undefined;
                this._routeColor = (this._options.routeColor) ? this._options.routeColor : routeColorFromContentStyle ? routeColorFromContentStyle : '#123EC4';
            }

            const routeLayer = {
                id: this._routeLayerName,
                type: "line",
                source: this._routeSourceName,
                layout: {
                    "line-join": "round",
                    "line-cap": "round",
                    "visibility": vis
                },
                paint: {
                    "line-color": this._routeColor,
                    // $FlowFixMe
                    "line-width": this._routeWidth,
                    "line-opacity": this._routePathOpacity,
                    "line-offset": 0
                }
            };
            map.addLayer(routeLayer);
        }

        if (this._showArrows === true) {
            this._arrowLayer.addLayer(this._options.arrowOptions);
        }

        if (this._showStops === true) {
            // Wait for all icons to load before adding the layer
            Promise.all([
                this._loadIcon(this._startIconName, this._originIconOptions.url, defaultOriginIconOptions.url),
                this._loadIcon(this._endIconName, this._destinationIconOptions.url, defaultDestIconOptions.url),
                this._loadIcon(this._stopIconName, this._stopIconOptions.url, defaultStopIconOptions.url),
            ]).then(() => {
                //check for stop label layer
                if (map.getLayer(this._stopLayerName)) {
                    warnOnce('Stop Layer exists');
                } else {
                    // $FlowFixMe
                    const stopLayer = {
                        layout: {
                            "icon-allow-overlap": true,
                            "symbol-sort-key": [
                                "case",
                                ["==", ["get", "stopType"], "stop"], 0,
                                ["==", ["get", "stopType"], "end"], 2,
                                1
                            ],
                            // $FlowFixMe
                            "icon-size": {
                                "type": "categorical",
                                "property": "stopType",
                                "stops": [
                                    ["start", this._originIconOptions.size],
                                    ["end", this._destinationIconOptions.size],
                                    ["stop", this._stopIconOptions.size]
                                ]
                            },
                            // $FlowFixMe
                            "icon-offset": {
                                "type": "categorical",
                                "property": "stopType",
                                "stops": [
                                    ["start", this._originIconOptions.offset],
                                    ["end", this._destinationIconOptions.offset],
                                    ["stop", this._stopIconOptions.offset],
                                ]
                            },
                            "text-field": "{stopLabel}",
                            "text-offset": [0.0, 0.15],
                            "text-allow-overlap": true,
                            "text-size": 10.5,
                            "visibility": vis
                        },
                        paint: {
                            'icon-opacity': { // for png and svg
                                "type": "categorical",
                                "property": "stopType",
                                "stops": [
                                    ["start", this._originIconOptions.opacity],
                                    ["end", this._destinationIconOptions.opacity],
                                    ["stop", this._stopIconOptions.opacity],
                                ]
                            },
                            'text-opacity': {
                                "type": "categorical",
                                "property": "stopType",
                                "stops": [
                                    ["start", this._originIconOptions.textOpacity],
                                    ["end", this._destinationIconOptions.textOpacity],
                                    ["stop", this._stopIconOptions.textOpacity],
                                ]
                            }
                        }
                    };
                    this.styleStopIcons(stopLayer);
                }
            });

        }
    }

    /**
     * Sets the route's ability to be modified by dragging
     * @param {boolean} isDraggable A boolean indicating whether or not the route is draggable.
     * @returns {Route} `this`
     */
    setDraggable(isDraggable: boolean) {
        this._options.isDraggable = isDraggable;
        if (this._options.hubRouting !== true && this._options.isDraggable === true) {
            this._dragRouteHandler.enable();
        } else {
            this._dragRouteHandler.disable();
        }
        return this;
    }

    _updateStopSource() {
        if (this._map) {
            //stops
            const stopSrc = this._map.getSource(this._stopSourceName);
            if (stopSrc) {
                stopSrc.setData(this._stopSourceData);
            } else {
                this._map.addSource(this._stopSourceName, {
                    type: 'geojson',
                    data: this._stopSourceData
                });
            }
        }
    }

    _updateSources() {
        if (this._map) {
            //stops
            this._updateStopSource();

            //route path
            const src = this._map.getSource(this._routeSourceName);
            if (src) {
                src.setData(this._routeSourceData);
            } else {
                this._map.addSource(this._routeSourceName, {
                    type: 'geojson',
                    data: this._routeSourceData
                });
            }

            if (this._options.frameRoute !== false) {
                this.frame();
            }
        }
    }

    _updateRouteColor(routeColor: string) {
        this._routeColor = routeColor;
        if (this._map) {
            //routeLayer
            this._map.setPaintProperty(this._routeLayerName, 'line-color', this._routeColor);
        }
    }

    _updateRoutePathOpacity(routePathOpacity: number) {
        this._routePathOpacity = routePathOpacity;
        if (this._map) {
            //routeLayer
            this._map.setPaintProperty(this._routeLayerName, 'line-opacity', this._routePathOpacity);
        }
    }

    _updateRouteWidth(routeWidth: RouteWidth | number) {
        this._routeWidth = routeWidth;
        if (this._map) {
            this._map.setPaintProperty(this._routeLayerName, 'line-width', this._routeWidth);
        }
    }

    _updateIconSize() {
        this._map.setLayoutProperty(this._stopLayerName, 'icon-size', [
            'case',
            ['==', ['get', 'stopType'], 'stop'], this._stopIconOptions.size,
            ['==', ['get', 'stopType'], 'end'], this._destinationIconOptions.size,
            this._originIconOptions.size
        ]);
    }

    _updateIconOffset() {
        this._map.setLayoutProperty(this._stopLayerName, 'icon-offset', [
            'match',
            ['get', 'stopType'],
            'stop',
            ['literal', this._stopIconOptions.offset],
            'end',
            ['literal', this._destinationIconOptions.offset],
            ['literal', this._originIconOptions.offset]
        ]);
    }

    _updateIconOpacity() {
        this._map.setPaintProperty(this._stopLayerName, 'icon-opacity', [
            'case',
            ['==', ['get', 'stopType'], 'stop'], this._stopIconOptions.opacity,
            ['==', ['get', 'stopType'], 'end'], this._destinationIconOptions.opacity,
            this._originIconOptions.opacity
        ]);
    }

    _updateTextOpacity() {
        this._map.setPaintProperty(this._stopLayerName, 'text-opacity', [
            'case',
            ['==', ['get', 'stopType'], 'stop'], this._stopIconOptions.textOpacity,
            ['==', ['get', 'stopType'], 'end'], this._destinationIconOptions.textOpacity,
            this._originIconOptions.textOpacity
        ]);
    }

    _generateStopData(hubRouting: boolean, stops: Array<Object>, viaPoints: ?Array<boolean>) {
        let features = [];
        if (stops.length > 0) {
            features = stops.map((stop, idx, arr) => {
                const s = (stop.lngLat) ? LngLat.convert(stop.lngLat) : LngLat.convert(stop);
                const p = {};
                if (idx === 0) {
                    p.stopType = 'start';
                    p.stopLabel = hubRouting === true ? 'H' : '';
                } else if (idx === arr.length - 1) {
                    p.stopType = hubRouting === true ? 'stop' : 'end';
                    p.stopLabel = hubRouting === true ? idx.toString() : '';
                } else {
                    p.stopType = 'stop';
                    p.stopLabel = idx.toString();
                    if (viaPoints !== null && viaPoints !== undefined &&
                        viaPoints.length === stops.length && viaPoints[idx] === true) {
                        // adding via point flag to a stop indicating it is introduced by drag routing
                        p.isViaPoint = viaPoints[idx];
                    }
                }
                return {
                    type: 'Feature',
                    properties: p,
                    geometry: {
                        type: 'Point',
                        coordinates: s.toArray()
                    }
                };
            });
        }
        this._stopSourceData = {
            type: 'FeatureCollection',
            features
        };
    }

    _getRoute(options: RouteOptions, viaPoints: ?Array<boolean>) {
        const hubRouting = options.hubRouting === true;
        const routeSourceData = {
            type: 'Feature',
            geometry: {
                type: 'MultiLineString',
                coordinates: [],
            }
        };
        let stops = options.stops;
        if (stops && stops.length === 1) {
            let stop = stops[0]; // LngLatLike
            if (stop.lngLat) { // StopOptions
                stop = LngLat.convert(stop.lngLat);
            }
            routeSourceData.geometry.coordinates.push([stop.lng, stop.lat]);
        }
        if (stops === undefined || stops === null || stops.length === 0) {
            stops = [];
        }
        if (stops.length < 2) {
            this._generateStopData(hubRouting, stops);
            this._routeSourceData = routeSourceData;
            this._updateSources();
            return;
        }
        const hasStopOptions = options.stops.some((stop) => stop.lngLat !== undefined);
        let makeRequest;
        let requestParameters;
        // using stop options requires POST
        if ((options.requestMethod === Common.RequestMethod.POST) || hasStopOptions) {
            makeRequest = postData;
            requestParameters = postRouteRequestParameters(this._routeId, options);
        } else {
            makeRequest = getJSON;
            requestParameters = getRouteRequestParameters(options);
        }
        this._generateStopData(hubRouting, options.stops, viaPoints);
        /**
         * Fired when a route starts loading. The event could fire before start listening.
         *
         * @event routeloading
         * @memberof Route
         * @instance
         * @type {Object}
         * @property {Event} route object that is start loading.
         */
        this.fire(new Event('routeloading'));
        makeRequest(requestParameters, (error, data) => {
            if (error) {
                error.name = 'route';
                this.fire(new ErrorEvent(error));
            } else if (data) {
                if ((options.requestMethod === Common.RequestMethod.POST) || hasStopOptions) {
                    data = JSON.parse(data);
                }

                this._routeSourceData = data;

                if (options.hubRouting !== true) {
                    // If the routeOptimization is enabled, the stops could be reordered
                    // The routePath call doesn't return an updated stop list. Instead of waiting for
                    // a report call to get the new stops we will use the legs of the route path to
                    // update the stop data source.
                    if ((options.routeOptimization) && (options.routeOptimization !== Common.RouteOptimization.NONE)) {
                        // $FlowFixMe
                        if (data.geometry && data.geometry.coordinates) {
                            const routeLegs = data.geometry.coordinates;
                            const newStops = [];
                            // pull origin from existing stop list
                            newStops.push(options.stops[0]);
                            if (routeLegs) {
                                routeLegs.forEach(leg => {
                                    // use last point from each leg
                                    newStops.push(leg[leg.length - 1]);
                                });
                                // update stop data source
                                this._generateStopData(hubRouting, newStops, viaPoints);
                            }
                        }
                    }
                }
                this._updateSources();
                /**
                 * Fired when a route has been returned.
                 *
                 * @event route
                 * @memberof Route
                 * @instance
                 * @type {Object}
                 * @property {Event} route object that has been returned.
                 */
                this.fire(new Event('route'));
            }
        });

        getRouteReports(this);
    }

    // Route events

    _onMouseEnter() {
        // use hoverCount to handle multiple enter/leave events across route line and stops
        this._hoverCount++;
        if (this._hoverCount === 1) {
            /**
             * Fired when a pointing device (usually a mouse) enters a visible portion of the route line or stop.
             *
             * @event mouseenter
             * @memberof Route
             * @instance
             * @type {Object}
             * @property {Event} target The route.
             */
            this.fire(new Event('mouseenter'));
        }
    }

    _onMouseLeave() {
        this._hoverCount--;
        if (this._hoverCount <= 0) {
            /**
             * Fired when a pointing device (usually a mouse) leaves a visible portion of the route line or stop.
             *
             * @event mouseleave
             * @memberof Route
             * @instance
             * @type {Object}
             * @property {Event} target The route.
             */
            this.fire(new Event('mouseleave'));
            this._hoverCount = 0;
        }
    }

    _onMouseClick(e: MapMouseEvent) {
        // Ignore second click if over both stop and route
        if (!e._defaultPrevented) {
            /**
             * Fired when a pointing device (usually a mouse) is pressed and released at the same point of the route line or stop.
             *
             * @event click
             * @memberof Route
             * @instance
             * @type {Object}
             * @property {Event} target The route.
             */
            this.fire(new Event('click'));
        }
        e.preventDefault();

    }

    _onStyleChange() {
        const originalValue = this._options.frameRoute;
        const styleChangeFrameRoute = this._options.styleChangeFrameRoute;
        try {
            if (styleChangeFrameRoute === true || styleChangeFrameRoute === false) {
                this._options.frameRoute = styleChangeFrameRoute;
            }
            this._updateSources();
        } catch (e) {
            throw new Error(e);
        } finally {
            this._options.frameRoute = originalValue;
        }
        this._initMap(this._map);
    }

    /**
     * Fired when a route is altered by dragging. Instead of letting the API handles the stop insertion, user utilizes a custom event listener to insert the stop. This event is not available when drag route is disabled.
     *
     * @event stopInserting
     * @memberof Route
     * @instance
     * @type {Object}
     * @property {Event} event a new stop and available insertion positions.
     * @example
     * // Create a route
     * const myRoute = new TrimbleMaps.Route(routeOptions);
     * // Set an event listener
     * myRoute.on('stopInserting', function(e) {
     *   myRoute.getRouteWithNewStop(e.newStop, e.routeLegPositions[e.routeLegPositions.length - 1]);
     * });
     *
     */
}
